import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { BranchContractor } from "@/models/api/administration/contractors/ContractorBranchResponse";
import {
  addContractorToTheBranch,
  getBranchContractors,
  removeContractorFromTheBranch,
} from "@/requests/administration/contractors/contractorBranch";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";

export interface AdministrationContractorBranchInterface {
  isLoading: boolean;
  isDialog: boolean;
  branchContractors: Array<BranchContractor>;
  parentContractor: BranchContractor | null;
}

export const AdministrationContractorBranchState: AdministrationContractorBranchInterface = {
  isLoading: false,
  isDialog: false,
  branchContractors: [],
  parentContractor: null,
};

const AdministrationContractorBranchModule = defineModule({
  state: AdministrationContractorBranchState as AdministrationContractorBranchInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setIsLoading(state, param: boolean) {
      state.isLoading = param;
    },
    setIsDialog(state, param: boolean) {
      if (!state.parentContractor) {
        state.isDialog = param;
      }
    },
    setBranchContractors(state, param: Array<BranchContractor>) {
      state.branchContractors = param;
    },
    setParentContractors(state, param: BranchContractor | null) {
      state.parentContractor = param;
    },
  },
  actions: {
    async downloadContractorBranch(context) {
      const { rootState, commit } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        commit.setIsLoading(true);
        const result = await getBranchContractors(rootState.administration.contractors.list.selectedContractorId);
        if (result.parentContractorId) {
          commit.setParentContractors({
            branchContractorId: result.parentContractorId,
            branchContractorShortName: result.parentContractorShortName,
            branchContractorName: result.parentContractorName,
            branchContractorTIN: result.parentContractorTIN,
          });
          commit.setBranchContractors([]);
        } else {
          commit.setParentContractors(null);
          commit.setBranchContractors(result.branchContractors);
        }
        commit.setIsLoading(false);
      }
    },
    async addContractoToTheBranch(context, contractorId: Array<string>) {
      const { rootState, dispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await addContractorToTheBranch(rootState.administration.contractors.list.selectedContractorId, contractorId);
        dispatch.downloadContractorBranch();
      }
    },
    async removeContractorFromTheBranch(context, contractorId: string) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      rootDispatch.failIfRequestsInProgress();
      const isRemoveConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(i18n.t("areYouSureYouWantToRemoveThisContractorFromThisBranch")),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "500px"
        )
      )) as boolean;
      if (isRemoveConfirmed) {
        await removeContractorFromTheBranch(contractorId);
        dispatch.downloadContractorBranch();
      }
    },
  },
});

export default AdministrationContractorBranchModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationContractorBranchModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationContractorBranchModule);
